import {SingleChoiceOption, isPlainSingleSelect} from 'common/components/select/selectParts'
import React, {FC} from 'react'
import Select, {components} from 'react-select'

const RSValueContainer: any = components.ValueContainer as any
const RSOption: any = components.Option as any

const SELECT_ALL_VALUE = '__select_all__'

// Compact multi-select display (same pattern as timetable constraints selects):
// show the first 2 selected labels and collapse the rest into "+N".
export const MultiValueContainer = (props: any) => {
  const {children, isMulti, getValue} = props

  if (!isMulti) {
    return <RSValueContainer {...(props as any)} />
  }

  const values = getValue().filter((v: any) => v.value !== SELECT_ALL_VALUE)

  const childrenToRender = React.Children.toArray(children).filter((child: any) => {
    if (!React.isValidElement(child)) return false
    return child.type === components.Input || child.type === components.Placeholder
  })

  return (
    <RSValueContainer {...(props as any)}>
      {!props.selectProps.inputValue && values.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {values.slice(0, 2).map((v: any) => {
            const label = props.selectProps.getOptionLabel(v)
            return (
              <span
                key={v.value ?? label}
                className='step4-subject-chip'
                title={label}
                style={{
                  display: 'inline-block',
                  flexShrink: 1,
                  minWidth: 40,
                  maxWidth: 110,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            )
          })}
          {values.length > 2 && (
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--bs-gray-600)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              +{values.length - 2}
            </span>
          )}
        </div>
      )}
      {childrenToRender}
    </RSValueContainer>
  )
}

export const CheckboxOption = (props: any) => {
  if (props.data?.value === SELECT_ALL_VALUE) {
    const allOptions = (props.options || []).filter((opt: any) => opt.value !== SELECT_ALL_VALUE)
    const selectedValues = props.getValue ? props.getValue().map((v: any) => v.value) : []
    const allSelected =
      allOptions.length > 0 && allOptions.every((opt: any) => selectedValues.includes(opt.value))

    return (
      <RSOption {...props}>
        <div className='form-check form-check-sm form-check-solid'>
          <input className='form-check-input' type='checkbox' checked={allSelected} readOnly />
          <label className='form-check-label fw-bold'>{props.label}</label>
        </div>
      </RSOption>
    )
  }

  if (isPlainSingleSelect(props)) return <SingleChoiceOption {...props} />

  return (
    <RSOption {...props}>
      <div className='form-check form-check-sm form-check-solid'>
        <input className='form-check-input' type='checkbox' checked={props.isSelected} readOnly />
        <label className='form-check-label'>{props.label}</label>
      </div>
    </RSOption>
  )
}

export type GenericTableColumn = {
  key: string
  label: React.ReactNode
  type: 'text' | 'select' | 'multi-select'
  options?: string[]
  thStyle?: React.CSSProperties
  tdStyle?: React.CSSProperties
  thClassName?: string
  tdClassName?: string
  render?: (params: {
    value: any
    row: Record<string, any>
    rowIndex: number
    onChange: (value: any) => void
  }) =>
    | React.ReactNode
    | {
        node: React.ReactNode
        tdProps?: React.TdHTMLAttributes<HTMLTableCellElement>
        hidden?: boolean
      }
}

interface Props {
  isDark: boolean
  data: Record<string, any>[]
  columns: GenericTableColumn[]
  onChange: (rowIndex: number, key: string, value: any) => void
  hashLabel: string
  selectPlaceholder: string
  containerClassName?: string
  containerStyle?: React.CSSProperties
  tableClassName?: string
  tableStyle?: React.CSSProperties
  theadClassName?: string
  theadStyle?: React.CSSProperties
  headerRowClassName?: string
  headerRowStyle?: React.CSSProperties
  hashThClassName?: string
  hashThStyle?: React.CSSProperties
  hashTdClassName?: string
  hashTdStyle?: React.CSSProperties
  tbodyStyle?: React.CSSProperties
  rowClassName?: (row: Record<string, any>, rowIndex: number) => string | undefined
  rowKey?: (row: Record<string, any>, rowIndex: number) => React.Key
  emptyState?: React.ReactNode
  emptyRowClassName?: string
  emptyTdClassName?: string
  emptyTdStyle?: React.CSSProperties
  /**
   * Index of `data[0]` in the caller's full list, for a paged slice: every `rowIndex` this table
   * hands out (renderers, `onChange`, `rowKey`, `rowClassName`, the # column) is then the
   * full-list index.
   */
  indexOffset?: number
}

const GenericTable: FC<Props> = ({
  isDark,
  data,
  columns,
  onChange,
  hashLabel,
  selectPlaceholder,
  containerClassName = 'table-responsive card border shadow-none mb-8',
  containerStyle,
  tableClassName = 'table table-row-bordered table-row-gray-300 gy-4 gs-4 align-middle mb-0',
  tableStyle,
  theadClassName,
  theadStyle,
  headerRowClassName = 'fw-bold text-muted',
  headerRowStyle,
  hashThClassName = 'w-50px',
  hashThStyle,
  hashTdClassName = 'text-muted fw-bold',
  hashTdStyle,
  tbodyStyle,
  rowClassName,
  rowKey,
  emptyState,
  emptyRowClassName,
  emptyTdClassName,
  emptyTdStyle,
  indexOffset = 0,
}) => {
  const resolvedTheadClassName = theadClassName ?? (isDark ? 'bg-dark' : 'bg-light')

  return (
    <div className={containerClassName} style={containerStyle}>
      <table className={tableClassName} style={tableStyle}>
        <thead className={resolvedTheadClassName} style={theadStyle}>
          <tr className={headerRowClassName} style={headerRowStyle}>
            <th className={hashThClassName} style={hashThStyle}>
              {hashLabel}
            </th>
            {columns.map((col) => (
              <th key={col.key} className={col.thClassName} style={col.thStyle}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={tbodyStyle}>
          {data.length === 0 && emptyState ? (
            <tr className={emptyRowClassName}>
              <td colSpan={columns.length + 1} className={emptyTdClassName} style={emptyTdStyle}>
                {emptyState}
              </td>
            </tr>
          ) : (
            data.map((row, pageIndex) => {
              const rowIndex = indexOffset + pageIndex
              return (
              <tr
                key={rowKey ? rowKey(row, rowIndex) : rowIndex}
                className={rowClassName?.(row, rowIndex)}
              >
                <td className={hashTdClassName} style={hashTdStyle}>
                  {rowIndex + 1}
                </td>
                {columns.map((col) => {
                  const rawValue = row?.[col.key]
                  const onCellChange = (value: any) => onChange(rowIndex, col.key, value)
                  const multiSelectValues = Array.isArray(rawValue)
                    ? rawValue.map((item: any) => {
                        const value =
                          typeof item === 'string'
                            ? item
                            : (item?.name ?? item?.value ?? String(item ?? ''))
                        return {value, label: value}
                      })
                    : []

                  const rendered = col.render
                    ? col.render({
                        value: rawValue,
                        row,
                        rowIndex,
                        onChange: onCellChange,
                      })
                    : null
                  const isObj =
                    rendered &&
                    typeof rendered === 'object' &&
                    !Array.isArray(rendered) &&
                    Object.prototype.hasOwnProperty.call(rendered as any, 'node')
                  const node = isObj ? (rendered as any).node : rendered
                  const tdProps = isObj ? (rendered as any).tdProps : undefined
                  const hidden = isObj ? Boolean((rendered as any).hidden) : false

                  if (hidden) return null

                  return (
                    <td
                      key={col.key}
                      className={col.tdClassName}
                      style={col.tdStyle}
                      {...(tdProps || {})}
                    >
                      {col.render ? (
                        node
                      ) : col.type === 'text' ? (
                        <input
                          type='text'
                          className='form-control form-control-solid'
                          value={rawValue || ''}
                          onChange={(e) => onCellChange(e.target.value)}
                        />
                      ) : col.type === 'multi-select' ? (
                        (() => {
                          const allOptions = (col.options || []).map((opt) => ({
                            value: opt,
                            label: opt,
                          }))
                          const selectAllOption = {value: SELECT_ALL_VALUE, label: 'Select All'}
                          const allSelected =
                            allOptions.length > 0 &&
                            allOptions.every((opt) =>
                              multiSelectValues.some((v) => v.value === opt.value)
                            )
                          return (
                            <Select
                              isMulti
                              options={[selectAllOption, ...allOptions]}
                              value={multiSelectValues}
                              onChange={(selectedOptions: any, actionMeta: any) => {
                                if (
                                  actionMeta.action === 'select-option' &&
                                  actionMeta.option?.value === SELECT_ALL_VALUE
                                ) {
                                  onCellChange(
                                    allSelected ? [] : allOptions.map((opt) => opt.value)
                                  )
                                } else if (
                                  actionMeta.action === 'deselect-option' &&
                                  actionMeta.option?.value === SELECT_ALL_VALUE
                                ) {
                                  onCellChange([])
                                } else {
                                  const selectedValues = selectedOptions
                                    ? selectedOptions
                                        .filter((opt: any) => opt.value !== SELECT_ALL_VALUE)
                                        .map((opt: any) => opt.value)
                                    : []
                                  onCellChange(selectedValues)
                                }
                              }}
                              components={{
                                ValueContainer: MultiValueContainer,
                                Option: CheckboxOption,
                              }}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              className='react-select-styled'
                              classNamePrefix='react-select'
                              placeholder={selectPlaceholder}
                              styles={{
                                control: (baseStyles, state) => ({
                                  ...baseStyles,
                                  backgroundColor: 'var(--bs-gray-100)',
                                  borderColor: state.isFocused ? '#B5B5C3' : 'transparent',
                                  minHeight: '43px',
                                }),
                                valueContainer: (baseStyles) => ({
                                  ...baseStyles,
                                  flexWrap: 'nowrap',
                                }),
                                option: (baseStyles, state: any) => ({
                                  ...baseStyles,
                                  // No bluish selected background — the checkbox tick
                                  // is the only selection indicator.
                                  backgroundColor: state.isFocused
                                    ? isDark
                                      ? '#323248'
                                      : '#F4F5F7'
                                    : 'transparent',
                                  color: isDark ? '#ffffff' : '#181C32',
                                  ':active': {
                                    backgroundColor: isDark ? '#323248' : '#F4F5F7',
                                  },
                                  ...(state.data?.value === SELECT_ALL_VALUE
                                    ? {fontWeight: 600}
                                    : {}),
                                }),
                              }}
                            />
                          )
                        })()
                      ) : (
                        <select
                          className='form-select form-select-solid'
                          value={rawValue || ''}
                          onChange={(e) => onCellChange(e.target.value)}
                        >
                          <option value=''>{selectPlaceholder}</option>
                          {(col.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  )
                })}
              </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

export default GenericTable
